"use client";

import Image from "next/image";
import { Button } from "@/components/ui/button";

interface Feature61Props {
  imagePosition?: "left" | "right";
  image?: string;
  title?: string;
  titleAs?: "h2" | "h3";
  description?: React.ReactNode;
  showButton?: boolean;
  buttonText?: string;
  buttonHref?: string;
  backgroundColor?: string;
}

const Feature61 = ({ 
  imagePosition = "right",
  image = "https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-1.svg",
  title = "Feature name",
  titleAs: TitleTag = "h3",
  description = "Lorem ipsum dolor sit amet consectetur adipisicing elit. Elig doloremque mollitia fugiat omnis! Porro facilis quo animi consequatur. Explicabo.",
  showButton = false,
  buttonText = "Learn More",
  buttonHref = "#",
  backgroundColor = "white"
}: Feature61Props) => {
  const flexDirection = imagePosition === "right" ? "flex-row-reverse" : "flex-row";
  // When image is on the right, text is on the left → add substantial left padding
  // When image is on the left, text is on the right → add substantial right padding
  const paddingClass = imagePosition === "right" ? "lg:pl-28 lg:pr-16 2xl:pl-44" : "lg:pr-40 lg:pl-16 2xl:pr-48";

  const handleButtonClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Check if href is a hash link (starts with #)
    if (buttonHref?.startsWith("#") && buttonHref.length > 1) {
      e.preventDefault();
      const targetId = buttonHref.substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    // If it's just "#" or not a hash link, let default behavior happen
  };

  return (
    <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-16" style={{ backgroundColor }}>
      <div className="w-full max-w-[75rem] mx-auto">
        <div className={`lg:flex ${flexDirection}`}>
          <div className="lg:w-1/2 flex justify-center mb-8 lg:mb-0">
            <div className="w-full max-w-lg relative aspect-[4/5]">
              <Image
                src={image}
                alt="Feature image"
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 512px"
                quality={75}
                className="border-border border object-cover"
                referrerPolicy="no-referrer-when-downgrade"
                loading="lazy"
                fetchPriority="low"
              />
            </div>
          </div>
          <div className={`lg:flex lg:w-1/2 lg:items-center px-4 sm:px-6 lg:px-0 ${paddingClass}`}>
            <div>
              <TitleTag className="mb-3 text-xl sm:text-2xl md:mb-4 md:text-3xl lg:mb-6">
                {title}
              </TitleTag>
              <div className="text-muted-foreground text-sm sm:text-base leading-loose mb-6">
                {description}
              </div>
              {showButton && (
                <Button 
                  size="lg"
                  className="text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#193144' }}
                  asChild
                >
                  <a 
                    href={buttonHref} 
                    onClick={handleButtonClick}
                    target={buttonHref && !buttonHref.startsWith('#') ? '_blank' : undefined}
                    rel={buttonHref && !buttonHref.startsWith('#') ? 'noopener noreferrer' : undefined}
                  >
                    {buttonText}
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { Feature61 };

